'use client';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import useCurrencyStore, { Currency } from '@/store/useCurrencyStore';
import { motion } from 'framer-motion';
import { CircleFlag } from 'react-circle-flags';
import { useTranslation } from 'react-i18next';

const currencies: { code: Currency; flag: string; symbol: string }[] = [
  { code: 'BRL', flag: 'br', symbol: 'R$' },
  { code: 'USD', flag: 'us', symbol: '$' },
];

interface CurrencyToggleProps {
  className?: string;
}

export function CurrencyToggle({ className }: CurrencyToggleProps) {
  const { t } = useTranslation('common');
  const { currency, setCurrency } = useCurrencyStore();

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'relative flex items-center gap-0.5 p-0.5 rounded-full bg-elevated/90 border border-colorBorder/30',
              className
            )}
          >
            {currencies.map((curr) => {
              const isActive = currency === curr.code;
              return (
                <button
                  key={curr.code}
                  onClick={() => setCurrency(curr.code)}
                  className={cn(
                    'relative z-10 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200',
                    isActive
                      ? 'text-black'
                      : 'text-colorText/60 hover:text-colorText'
                  )}
                >
                  {isActive && (
                    <motion.div
                      layoutId="currency-toggle-active"
                      className="absolute inset-0 bg-[#84cc16] rounded-full"
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}
                  <span className="relative z-10 flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-full overflow-hidden shrink-0">
                      <CircleFlag countryCode={curr.flag} height={16} />
                    </div>
                    <span className="hidden sm:inline">{curr.code}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-elevated border-colorBorder">
          <p className="text-xs">{t('currency_tooltip')}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
